import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchBuyerById,
  updateBuyer,
} from '../../../store/buyerAPI/buyer/reducers/buyer.slice';
import { AppDispatch, RootState } from '../../../store/store';
import { BuyerResponseDto } from '../../../store/buyerAPI/buyer/dto/response/buyer-response.dto';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { fetchCommodities } from '../../../store/tradeAPI/commodity/reducers/commodity.slice';
import { fetchLandscapes } from '../../../store/landscapeAPI/landscape/reducers/landscape.slice';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import { BuyerUpdateRequestDto } from '../../../store/buyerAPI/buyer/dto/request/buyer-update-request.dto';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';
import cn from 'classnames';
import styles from './OrganizationEditPage.module.css';
import { OrganizationEditTemplate } from '../../templates';
import Spinner from '../../atoms/Spinner/Spinner';
import { showSuccess } from '../../../utils/Notifications';

interface OrganizationEditPageProps {}

const OrganizationEditPage: FC<OrganizationEditPageProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { organizationId } = useParams();

  const [imageMeta, setImageMeta] = useState<ImageMeta>();
  const [logoMeta, setLogoMeta] = useState<ImageMeta>();
  const [organization, setOrganization] = useState<BuyerResponseDto>();
  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );
  const { status } = useSelector((state: RootState) => state.buyerSlice);

  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  useEffect(() => {
    dispatch(
      fetchBuyerById({ id: organizationId!, language: selectedLanguage })
    )
      .unwrap()
      .then((resp) => setOrganization(resp));
    dispatch(fetchCommodities());
    dispatch(fetchLandscapes());
  }, [dispatch, organizationId, selectedLanguage]);

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    if (!organization) return;
    const params: TranslateParams<BuyerResponseDto> = {
      obj: organization,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };

    const translatedResponse = (await dispatch(
      translateObject(params)
    ).unwrap()) as BuyerResponseDto;

    const updateRequest: BuyerUpdateRequestDto = {
      name: organization.name ?? '',
      logo: organization.logo ?? '',
      profileType: organization.profileType,
      description: translatedResponse.description ?? '',
      url: organization.url ?? '',
      backgroundImage: organization.backgroundImage,
      id: organization.id,
      published: organization.published,
    };

    await dispatch(
      updateBuyer({
        data: updateRequest,
        language: toLanguage,
      })
    ).unwrap();
    setLanguage(toLanguage);
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  const handleBackgroundOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const handleLogoOnChange = (imageMeta: ImageMeta) => {
    setLogoMeta(imageMeta);
  };

  const handleOnChange = (data: BuyerResponseDto) => {
    setOrganization(data);
  };

  const processMedia = async (
    file: string,
    meta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (!meta) {
      return { href: file };
    }
    const formData = new FormData();
    formData.append(
      'file',
      new Blob([file], {
        type: meta.imageType ?? 'image/png',
      }),
      meta.imageSrc
    );
    const response = await dispatch(saveMedia(formData));
    return response.payload as MediaSaveResponseDto;
  };

  const onSubmit = async (data: BuyerResponseDto) => {
    const logo = await processMedia(data.logo ?? '', logoMeta);
    const image = await processMedia(data.backgroundImage ?? '', imageMeta);

    const organizationToUpdate: BuyerUpdateRequestDto = {
      id: data.id,
      published: data.published,
      name: data.name ?? '',
      logo: logo.href,
      description: data.description ?? '',
      profileType: data.profileType,
      backgroundImage: image.href,
      url: data.url ?? '',
    };

    await dispatch(
      updateBuyer({ data: organizationToUpdate, language: selectedLanguage })
    );

    showSuccess('Organization saved successfully.');
    navigate('/organizations');
  };

  if (
    status === 'loading' ||
    mediaStatus === 'loading' ||
    translateStatus === 'loading'
  ) {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      {organization && (
        <OrganizationEditTemplate
          handleOnChange={handleOnChange}
          data={organization}
          onSubmit={onSubmit}
          handleBackgroundOnChange={handleBackgroundOnChange}
          handleLogoOnChange={handleLogoOnChange}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
    </div>
  );
};

export default OrganizationEditPage;
